import { Injectable } from '@angular/core';
import { CanMatch, Route, UrlSegment } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, timeout } from 'rxjs/operators';

import { FlagFallbackCacheService } from '@services/flag-fallback-cache.service';
import { LaunchDarklyService } from '@services/launchdarkly.service';
import { Logger, LoggingService } from '@services/logging.service';
import { UserService } from '@services/user.service';

export const DATA_CATEGORIZATION_REDESIGN_FLAG = 'data-categorization-redesign';

// How long to wait for LaunchDarkly to settle the identified user's flags
// before falling back to the last value read for this user (or the dev path).
const FLAGS_READY_TIMEOUT_MS = 4000;

type FallbackReason = 'timeout' | 'error' | 'identify-failed';

/**
 * Route `canMatch` guard that selects the redesigned (v2) Data Categorization
 * page when the `data-categorization-redesign` flag is on, and otherwise lets
 * routing fall through to the legacy (dev) page.
 *
 * The flag is targeted per tenant, so it is only correct after the LaunchDarkly
 * client has identified the user. Identification is triggered by the route's
 * `AuthMiddleware.canActivate`, which runs AFTER `canMatch` — so this guard
 * triggers it itself via `getCurrentUser()`, then waits for `flagsReady$` (the
 * identified-context signal, with a bounded timeout) before reading the flag
 * once. Without this, a hard refresh would read the anonymous bootstrap flags
 * (target rule not applied) and incorrectly fall through to the legacy page.
 *
 * The guard is not re-evaluated for a mounted component, so the chosen version
 * is fixed for the lifetime of the page and only changes on the next navigation.
 */
@Injectable({ providedIn: 'root' })
export class DataCategorizationRedesignMatch implements CanMatch {
  private logger: Logger;

  constructor(
    private _launchDarklyService: LaunchDarklyService,
    private _userService: UserService,
    loggingService: LoggingService,
    private _flagFallbackCache: FlagFallbackCacheService,
  ) {
    this.logger = loggingService.rootLogger.newLogger('DataCategorizationRedesignMatch');
  }

  canMatch(_route: Route, _segments: UrlSegment[]): Observable<boolean> {
    // Ensure the user is identified before reading the targeted flag. On a hard
    // refresh nothing has identified the user yet (canActivate runs later), so
    // we trigger it here; `getCurrentUser()` is cached, so AuthMiddleware's
    // later call reuses the same user without a second request.
    const startedAt = Date.now();
    let hasUserResolved = false;
    return this._userService.getCurrentUser().pipe(
      tap(() => (hasUserResolved = true)),
      switchMap(() => this._launchDarklyService.flagsReady$),
      filter(ready => ready),
      take(1),
      map(() => this._readFlag(startedAt)),
      // Fallback must come AFTER the map, so the timeout's fallback is the final
      // value. (With map last, it would be overwritten by re-reading the flag —
      // which is `true` when the flag is on.)
      timeout({
        first: FLAGS_READY_TIMEOUT_MS,
        with: () => this._fallBackToLastKnown('timeout', startedAt, hasUserResolved),
      }),
      // Identification failure must not break routing — fall back to the last known value.
      catchError(() => this._fallBackToLastKnown('error', startedAt, hasUserResolved)),
    );
  }

  private _readFlag(startedAt: number): boolean {
    const identifyStatus = this._launchDarklyService.identifyDiagnostics.status;
    if (identifyStatus === 'failed') {
      return this._resolveFallbackValue('identify-failed', startedAt, true);
    }
    const isOn = !!this._launchDarklyService.flags[DATA_CATEGORIZATION_REDESIGN_FLAG];
    if (identifyStatus === 'resolved') {
      this._flagFallbackCache.set(this._userService.user, DATA_CATEGORIZATION_REDESIGN_FLAG, isOn);
    }
    if (!isOn) {
      this.logger.info('Data categorization redesign flag read as off', this._buildDiagnostics(startedAt, true));
    }
    return isOn;
  }

  private _fallBackToLastKnown(reason: FallbackReason, startedAt: number, hasUserResolved: boolean): Observable<boolean> {
    return of(this._resolveFallbackValue(reason, startedAt, hasUserResolved));
  }

  private _resolveFallbackValue(reason: FallbackReason, startedAt: number, hasUserResolved: boolean): boolean {
    const cachedValue = this._flagFallbackCache.get(this._userService.user, DATA_CATEGORIZATION_REDESIGN_FLAG);
    this.logger.info('Data categorization redesign guard used its fallback value', {
      reason,
      usedCachedValue: cachedValue !== undefined,
      ...this._buildDiagnostics(startedAt, hasUserResolved),
    });
    return cachedValue ?? false;
  }

  private _buildDiagnostics(startedAt: number, hasUserResolved: boolean): object {
    return {
      guardElapsedMs: Date.now() - startedAt,
      hasUserResolved,
      identify: this._launchDarklyService.identifyDiagnostics,
    };
  }
}
